<template>
    <div class="p-t-4">
        <div v-if="questions.length > 0">
            <section class="section p-t-6 p-b-6">
                <div class="panel row">
                    <div class="panel-aside columns small-12 medium-3 large-4 p-r-6">
                        <div class="f-s-5 m-b-2 p-b-1">{{ trans('Questions') }}</div>
                        <p class="c-muted f-s-3 m-b-1">{{ trans('Build your quiz questions manuualy or generate them with AI from your lesson content. Drag to reorder. All questions must be anwsered by the member before they can submit.') }}</p>
                    </div>
                    <div class="panel-content columns small-12 medium-9 large-8">
                        <div class="row row-collapse row-middle m-b-2">
                            <div class="column">
                                <h3 class="f-s-4 f-w-bold m-b-0">{{ questionsCountLabel }}</h3>
                                <p class="f-s-2 c-muted m-b-0" v-if="lastEditedAt">{{ trans('Last edited :time', {time: timeAgo(lastEditedAt)}) }}</p>
                            </div>
                            <div class="column text-right">
                                <button type="button" class="btn btn-primary" v-on:click="addQuestion">{{ trans('Add question') }}</button>
                                <button type="button" class="btn btn-outline btn-outline--bright m-l-2" v-on:click="openGenerateModal">{{ trans('Generate with AI') }}</button>
                            </div>
                        </div>

                        <div v-sortable="{ onEnd: onReorder, handle: '.handle' }" v-bind:data-quiz-id="quiz.id">
                            <quiz-question-card
                                v-for="(question, index) in questions"
                                v-bind:key="question.key"
                                v-bind:question="question"
                                v-bind:index="index"
                                v-bind:quiz="quiz"
                                v-bind:expanded="expandedKey === question.key"
                                v-bind:data-encoded-id="question.encoded_id"
                                v-on:toggle-expand="toggleExpanded(question.key)"
                                v-on:saved="onSaved(index, $event)"
                                v-on:discard-new="onDiscardNew(index)"
                                v-on:deleted="onDeleted(index, $event)"
                                v-on:duplicated="onDuplicated(index, $event)"
                            ></quiz-question-card>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        <div v-else>
            <section class="section p-t-6 p-b-6">
                <div class="panel row">
                    <div class="panel-aside columns small-12 medium-3 large-4 p-r-6">
                        <div class="f-s-5 m-b-2 p-b-1">{{ trans('Questions') }}</div>
                        <p class="c-muted f-s-3 m-b-1">{{ trans('Build your quiz questions manuualy or generate them with AI from your lesson content. Drag to reorder. All questions must be anwsered by the member before they can submit.') }}</p>
                    </div>
                    <div class="panel-content columns small-12 medium-9 large-8">
                        <div class="row">
                            <div class="column">
                                <a href="#create-manually" v-on:click.prevent="addQuestion" class="br block card card-mini h-anim h-bg-accent--light">
                                    <div class="card-item">
                                        <div class="cover-container uploads uploads-r-16_9">
                                            <div class="cover row row-middle upload-content">
                                                <div class="cover-content column text-center">
                                                    <div class="f-s-4 f-w-bold m-b-1">{{ trans('Create manually') }}</div>
                                                    <p class="c-muted f-s-3 m-b-0">{{ trans('Build a question yourself: choose a type, write the question, and add answer options.') }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                            <div class="column">
                                <a href="#create-with-ai" v-on:click.prevent="openGenerateModal" class="br block card card-mini h-anim h-bg-accent--light">
                                    <div class="card-item">
                                        <div class="cover-container uploads uploads-r-16_9">
                                            <div class="cover row row-middle upload-content">
                                                <div class="cover-content column text-center">
                                                    <div class="f-s-4 f-w-bold m-b-1">{{ trans('Create with AI') }}</div>
                                                    <p class="c-muted f-s-3 m-b-0">{{ trans('Pick lessons from this module and let AI draft the questions for you.') }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <quiz-generate-modal
            ref="generateModal"
            v-bind:quiz="quiz"
            v-bind:lessons="lessons"
            v-bind:has-ai-credits="hasAiCredits"
            v-on:generated="onGenerated"
        ></quiz-generate-modal>
    </div>
</template>
<script>
import Orderable from '../../mixins/Orderable.js';
import { EventBus } from '../../event-bus.js';
import QuizQuestionCard from './QuizQuestionCard.vue';
import QuizGenerateModal from '../modals/QuizGenerateModal.vue';

let uniqueKey = 0;

export default {
    components: {
        QuizQuestionCard,
        QuizGenerateModal
    },
    mixins: [Orderable],
    props: {
        quiz: {
            type: Object,
            required: true
        },
        lessons: {
            type: Array,
            default: () => []
        },
        hasAiCredits: {
            type: Boolean,
            default: true
        }
    },
    data() {
        const questions = (this.quiz.questions || []).map((question) => Object.assign({}, question, { key: ++uniqueKey }));

        return {
            questions,
            expandedKey: null,
            lastEditedAt: this.latestUpdatedAt(questions),
            model: 'Kourses\\Models\\QuizQuestion',
            orderableParentKey: 'quiz_id',
        }
    },
    computed: {
        questionsCountLabel() {
            return this.questions.length === 1
                ? this.trans('1 question')
                : this.trans(':count questions', {count: this.questions.length});
        }
    },
    created() {
        EventBus.$emit('quiz-questions:count-changed', this.questions.length);
    },
    methods: {
        timeAgo(date) {
            const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);

            if (seconds < 60) {
                return this.trans('just now');
            }

            const intervals = [
                [60 * 60 * 24 * 365, this.trans('year')],
                [60 * 60 * 24 * 30, this.trans('month')],
                [60 * 60 * 24, this.trans('day')],
                [60 * 60, this.trans('hour')],
                [60, this.trans('minute')],
            ];

            for (const [unitSeconds, label] of intervals) {
                const count = Math.floor(seconds / unitSeconds);

                if (count >= 1) {
                    return this.trans(':count :unit ago', {count, unit: count === 1 ? label : label + 's'});
                }
            }

            return this.trans('just now');
        },
        latestUpdatedAt(questions) {
            return questions
                .map((question) => question.updated_at)
                .filter((value) => value)
                .sort()
                .pop() || null;
        },
        updateMeta(meta) {
            if (meta.questions_count !== undefined) {
                EventBus.$emit('quiz-questions:count-changed', meta.questions_count);
            }

            if (meta.last_edited_at !== undefined) {
                this.lastEditedAt = meta.last_edited_at;
            }
        },
        addQuestion() {
            const question = {
                key: ++uniqueKey,
                id: null,
                encoded_id: null,
                type: 'single',
                question: '',
                image: null,
                image_url: false,
                options: [
                    {id: null, text: '', is_correct: false},
                    {id: null, text: '', is_correct: false},
                ],
            };

            this.questions.push(question);
            this.expandedKey = question.key;
        },
        toggleExpanded(key) {
            this.expandedKey = this.expandedKey === key ? null : key;
        },
        onSaved(index, data) {
            this.questions.splice(index, 1, Object.assign({}, data.question, { key: this.questions[index].key }));
            this.expandedKey = null;
            this.updateMeta(data);
        },
        onDiscardNew(index) {
            this.questions.splice(index, 1);
            this.expandedKey = null;
        },
        onDeleted(index, meta) {
            this.questions.splice(index, 1);
            this.expandedKey = null;
            this.updateMeta(meta);
        },
        onDuplicated(index, data) {
            this.questions.splice(index + 1, 0, Object.assign({}, data.question, { key: ++uniqueKey }));
            this.updateMeta(data);
        },
        onReorder(event) {
            if (event.oldIndex !== event.newIndex) {
                const [moved] = this.questions.splice(event.oldIndex, 1);
                this.questions.splice(event.newIndex, 0, moved);
            }

            this.onUpdate(event);
        },
        openGenerateModal() {
            this.$refs.generateModal.open();
        },
        onGenerated(data) {
            (data.questions || []).forEach((question) => {
                this.questions.push(Object.assign({}, question, { key: ++uniqueKey }));
            });

            this.updateMeta(data);
        },
    }
}
</script>
